// src/components/GoldChat.js
// Gold Tier: 1 session per week, 24hr window, timer starts on first message sent
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  getGoldSession, startGoldSession,
  getMessages, sendMessage, subscribeToMessages,
  SKY_BRI_USER_ID,
  markConversationRead, subscribeToReadReceipts
} from '../lib/supabase'
import { makePending, resolvePending, failPending, markSending, keepUnsent, applyReadReceipt } from '../lib/chatUtils'
import useChatScroll from '../hooks/useChatScroll'
import useTyping from '../hooks/useTyping'
import ChatThread, { JumpToLatest, TypingIndicator } from './ChatThread'
import ChatPhotoViewer from './ChatPhotoViewer'
import './Chat.css'

function pad(n) { return String(n).padStart(2, '0') }

function formatCountdown(ms) {
  if (ms <= 0) return '00:00:00'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

export default function GoldChat() {
  const { session } = useAuth()
  const userId = session?.user?.id

  const [goldSession, setGoldSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const [nextSessionMs, setNextSessionMs] = useState(0)
  const [loadingSession, setLoadingSession] = useState(true)
  const [error, setError] = useState('')
  const [viewerSrc, setViewerSrc] = useState(null)
  const now = Date.now()

  const sessionActive = goldSession &&
    goldSession.session_expires_at &&
    new Date(goldSession.session_expires_at).getTime() > now

  const { otherTyping, notifyTyping, clearTyping } = useTyping(sessionActive ? userId : null, userId)
  const { containerRef, bottomRef, onScroll, showJump, jumpToLatest } =
    useChatScroll(messages, userId, userId, otherTyping)

  const sessionsUsed = goldSession?.month_sessions_used || 0
  const monthExhausted = sessionsUsed >= 4

  // Load gold session state
  const loadSession = useCallback(async () => {
    if (!userId) return
    const { data } = await getGoldSession(userId)
    setGoldSession(data)
    setLoadingSession(false)
  }, [userId])

  useEffect(() => { loadSession() }, [loadSession])

  // Load existing messages
  useEffect(() => {
    if (!userId || !sessionActive) return
    getMessages(userId).then(({ data }) => {
      if (!data) return
      setMessages(prev => keepUnsent(data, prev))
      if (!document.hidden && data.some(m => m.sender_id !== userId && m.read_at === null)) {
        markConversationRead(SKY_BRI_USER_ID)
      }
    })
  }, [userId, sessionActive])

  // Subscribe to incoming messages (real-time)
  useEffect(() => {
    if (!userId || !sessionActive) return
    const sub = subscribeToMessages(userId, (payload) => {
      setMessages(prev => [...prev, payload.new])
      clearTyping()
      // They're looking at the chat right now, so this counts as read
      if (!document.hidden) markConversationRead(SKY_BRI_USER_ID)
    })
    // Sky Bri read one of my messages -> show "Seen" instantly
    const receipts = subscribeToReadReceipts(userId, payload =>
      setMessages(prev => applyReadReceipt(prev, payload.new)))
    const onVisible = () => { if (!document.hidden) markConversationRead(SKY_BRI_USER_ID) }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      sub.unsubscribe()
      receipts.unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [userId, sessionActive]) // eslint-disable-line

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (goldSession?.session_expires_at) {
        const exp = new Date(goldSession.session_expires_at).getTime()
        setTimeLeft(Math.max(0, exp - Date.now()))
      }
      // Next session: 7 days after session started
      if (goldSession?.session_started_at) {
        const nextWeek = new Date(goldSession.session_started_at).getTime() + 7 * 24 * 60 * 60 * 1000
        setNextSessionMs(Math.max(0, nextWeek - Date.now()))
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [goldSession])

  async function handleSend() {
    if (!input.trim() || sending) return
    setError('')

    // If no active session, start one now (timer begins here)
    if (!sessionActive) {
      if (monthExhausted) {
        setError('You have used all 4 sessions this month. Your access resets next month.')
        return
      }
      setSending(true)
      const { data, error: sessionError } = await startGoldSession(userId)
      if (sessionError) { setError(sessionError.message); setSending(false); return }
      setGoldSession(data)
    }

    setSending(false)
    const content = input.trim()
    setInput('')
    // Show it instantly; a failure becomes a visible "Not sent · Retry" instead of vanishing
    const pending = makePending({ senderId: userId, receiverId: SKY_BRI_USER_ID, content })
    setMessages(prev => [...prev, pending])
    deliver(pending.id, content)
  }

  // Send to the server and settle the on-screen bubble either way
  async function deliver(tempId, content) {
    const { data: msg, error: msgError } = await sendMessage({
      senderId: userId,
      receiverId: SKY_BRI_USER_ID,
      content
    })
    if (msgError || !msg) setMessages(prev => failPending(prev, tempId))
    else setMessages(prev => resolvePending(prev, tempId, msg))
  }

  function retryMessage(id) {
    const m = messages.find(x => x.id === id)
    if (!m) return
    setMessages(prev => markSending(prev, id))
    deliver(id, m.content)
  }
  function discardMessage(id) { setMessages(prev => prev.filter(x => x.id !== id)) }

  const weekDots = Array.from({ length: 4 }, (_, i) => i < sessionsUsed)

  if (loadingSession) {
    return <div className="chat-loading">Loading your session...</div>
  }

  return (
    <div className="chat-wrap">
      {/* SESSION STATUS BAR */}
      <div className="gold-status-bar">
        <div className="gold-status-bar__left">
          <span className="gold-status-bar__label">Weekly Sessions</span>
          <div className="gold-status-bar__dots">
            {weekDots.map((used, i) => (
              <div key={i} className={`gold-dot ${used ? 'gold-dot--used' : 'gold-dot--empty'}`} title={`Week ${i + 1}`} />
            ))}
          </div>
          <span className="gold-status-bar__used">{sessionsUsed}/4 this month</span>
        </div>

        {sessionActive && (
          <div className="gold-status-bar__timer">
            <span className="gold-status-bar__timer-label">Session closes in</span>
            <span className="gold-status-bar__timer-count">{formatCountdown(timeLeft)}</span>
          </div>
        )}

        {!sessionActive && goldSession?.session_started_at && !monthExhausted && (
          <div className="gold-status-bar__timer">
            <span className="gold-status-bar__timer-label">Next session in</span>
            <span className="gold-status-bar__timer-count">{formatCountdown(nextSessionMs)}</span>
          </div>
        )}
      </div>

      {/* LOCKED STATE */}
      {!sessionActive && (
        <div className="chat-locked">
          {monthExhausted ? (
            <>
              <div className="chat-locked__icon">◈</div>
              <h3>All 4 sessions used</h3>
              <p>Your sessions reset at the start of next month.<br />
                Upgrade to <strong>Premium</strong> for unlimited access.</p>
            </>
          ) : goldSession?.session_started_at ? (
            <>
              <div className="chat-locked__icon">◈</div>
              <h3>Session closed</h3>
              <p>Your 24-hour window has ended.<br />
                Next session opens in <strong>{formatCountdown(nextSessionMs)}</strong></p>
            </>
          ) : (
            <>
              <div className="chat-locked__icon">✦</div>
              <h3>Ready for your session?</h3>
              <p>You have <strong>{4 - sessionsUsed} session{4 - sessionsUsed !== 1 ? 's' : ''}</strong> remaining this month.<br />
                Type your first message below to start your 24-hour window.</p>
            </>
          )}
        </div>
      )}

      {/* MESSAGES */}
      {(sessionActive || messages.length > 0) && (
        <div className="chat-messages" id="chat-messages" ref={containerRef} onScroll={onScroll}>
          {messages.length === 0 && (
            <div className="chat-messages__empty">
              Say something — your 24-hour session starts now ✦
            </div>
          )}
          <ChatThread
            messages={messages}
            userId={userId}
            onRetry={retryMessage}
            onDiscard={discardMessage}
            onOpenImage={setViewerSrc}
          />
          <TypingIndicator show={otherTyping} name="Sky Bri" />
          <JumpToLatest show={showJump} onClick={jumpToLatest} />
          <div ref={bottomRef} />
        </div>
      )}

      {error && <div className="chat-error">{error}</div>}

      {/* INPUT */}
      <div className={`chat-input-row ${(!sessionActive && goldSession?.session_started_at && !monthExhausted === false) || monthExhausted ? 'chat-input-row--disabled' : ''}`}>
        <input
          className="chat-input"
          placeholder={
            monthExhausted ? 'Sessions exhausted for this month'
            : !sessionActive && goldSession?.session_started_at ? 'Waiting for next week...'
            : 'Ask Sky Bri anything — your 24hrs start when you send...'
          }
          value={input}
          onChange={e => { setInput(e.target.value); if (e.target.value) notifyTyping() }}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          disabled={monthExhausted || (!sessionActive && goldSession?.session_started_at && nextSessionMs > 0)}
        />
        <button
          className="chat-send btn btn--gold btn--sm"
          onClick={handleSend}
          disabled={sending || !input.trim() || monthExhausted || (!sessionActive && goldSession?.session_started_at && nextSessionMs > 0)}
        >
          {sending ? '...' : 'Send'}
        </button>
      </div>
      <ChatPhotoViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
    </div>
  )
}
